import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { topCalendarDatesAtomFamily, useTimezone } from "@ryde/shared";

import { dashboardAppliedFiltersAtom } from "../dynamic-dashboard.atoms";
import {
  DASHBOARD_ENTITY,
  DashboardChartType,
  IDashboardCard,
} from "../dynamic-dashboard.models";
import {
  defaultValueFormatter,
  EMPTY_CONTENT,
} from "./dashboard-card-data.constants";
import {
  fetchDashboardCardData,
  mapFetchError,
} from "./dashboard-card-data.fetch";
import {
  ICardDataFetchState,
  IDashboardCardDataResult,
} from "./dashboard-card-data.models";

export type {
  IDashboardCardDataResult,
  IDashboardCardDataStatus,
} from "./dashboard-card-data.models";

/**
 * Fetches a custom card's data through the reports engine
 * (`ReportsApi.fetchReportData`), which enforces report/field/row-level
 * permissions server-side, and reduces it to the chart content the dashboard
 * charts consume. Clustered/stacked cards with a `seriesKey` fetch two grouping
 * dimensions; Stat cards fetch the previous period too for a %-change.
 * Legacy default cards don't use this (they render the real dashboard card).
 */
export const useDashboardCardData = (
  card: IDashboardCard,
): IDashboardCardDataResult => {
  const isReportCard =
    card.chartType !== DashboardChartType.Legacy &&
    typeof card.reportId === "number" &&
    Boolean(card.dimensionKey) &&
    Boolean(card.metricKey);

  const calendarDates = useRecoilValue(
    topCalendarDatesAtomFamily(DASHBOARD_ENTITY),
  );
  const { t, i18n } = useTranslation();
  const { timezone } = useTimezone();
  const appliedFilters = useRecoilValue(dashboardAppliedFiltersAtom);
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(
    (): void => setReloadToken(token => token + 1),
    [],
  );

  const dimensionLabel = card.dimensionLabel ?? card.subtitle ?? "";
  const metricLabel = card.metricLabel ?? card.title ?? "";

  const [state, setState] = useState<ICardDataFetchState>(() => ({
    content: EMPTY_CONTENT,
    status: { loading: isReportCard, isEmpty: !isReportCard },
  }));

  const criteriaKey = useMemo(
    () =>
      JSON.stringify({
        id: card.reportId,
        dimensionKey: card.dimensionKey,
        seriesKey: card.seriesKey,
        metricKey: card.metricKey,
        groupingParams: card.groupingParams,
        periodGrouping: card.periodGrouping,
        chartType: card.chartType,
        dates: calendarDates,
        filters: appliedFilters,
        cardFilters: card.cardFilters,
        // Re-run when the UI language / timezone changes so already-loaded
        // dimension labels re-translate/re-format, and on an explicit retry.
        lang: i18n.language,
        timezone,
        reloadToken,
      }),
    [
      card.reportId,
      card.dimensionKey,
      card.seriesKey,
      card.metricKey,
      card.groupingParams,
      card.periodGrouping,
      card.chartType,
      calendarDates,
      appliedFilters,
      card.cardFilters,
      i18n.language,
      timezone,
      reloadToken,
    ],
  );

  useEffect(() => {
    // Non-report cards keep the initial empty state (set in useState) — no
    // fetch, and no redundant setState that would re-render for nothing.
    if (!isReportCard) {
      return undefined;
    }

    const abort = new AbortController();
    let cancelled = false;
    // Fresh status on each (re)fetch: clear any prior error/forbidden flags.
    setState(prev => ({ ...prev, status: { loading: true, isEmpty: false } }));

    fetchDashboardCardData({
      card,
      calendarDates,
      appliedFilters,
      t,
      timezone,
      abort,
    })
      .then(result => {
        if (!cancelled) {
          setState(result);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setState(mapFetchError(err));
        }
      });

    return () => {
      cancelled = true;
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- criteriaKey captures all inputs
  }, [criteriaKey]);

  return {
    content: state.content,
    multiSeries: state.multiSeries,
    stat: state.stat,
    dimensionLabel,
    metricLabel,
    status: state.status,
    valueFormatter: state.valueFormatter ?? defaultValueFormatter,
    reload,
  };
};
